import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Edit2, Save, X, Calendar as CalendarIcon, Plus, Mail, FileSpreadsheet, Trash2, Send, CheckCircle } from 'lucide-react';
import { strToU8, zipSync, unzipSync, strFromU8 } from 'fflate';
import axios from 'axios';

const API_BASE_URL = 'https://baustellenmgmt.mas123.de/api';

const STATUS_COLORS = {
  'ENTWURF': { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' },
  'EINGEREICHT': { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' },
  'GENEHMIGT': { bg: '#d1fae5', border: '#10b981', text: '#065f46' }
};

const STATUS_SYMBOLS = {
  'ENTWURF': { Icon: Edit2, label: 'Entwurf' },
  'EINGEREICHT': { Icon: Send, label: 'Eingereicht' },
  'GENEHMIGT': { Icon: CheckCircle, label: 'Genehmigt' }
};

function isOptionEntryForTag(stundenEntry, tagOptionEntry) {
  if (!stundenEntry || !tagOptionEntry?.option_details) return false;
  if (stundenEntry.baustelle != null) return false;
  const desc = stundenEntry.beschreibung || '';
  const opt = tagOptionEntry.option_details;
  const expectedPrefix = `${opt.kurz} - ${opt.name}`;
  return desc.startsWith(expectedPrefix);
}

function getBaustelleDisplayName(baustelle) {
  if (!baustelle) return 'N/A';
  return baustelle.name || baustelle.kunde_name || 'N/A';
}

function StundenerfassungView({ token }) {
  const [mitarbeiter, setMitarbeiter] = useState([]);
  const [subunternehmer, setSubunternehmer] = useState([]);
  const [selectedMitarbeiter, setSelectedMitarbeiter] = useState(null);
  const [tagOptionen, setTagOptionen] = useState([]);
  const [selectedTagOption, setSelectedTagOption] = useState('');
  const [tagOptionMode, setTagOptionMode] = useState(false);
  const [tagOptionEntries, setTagOptionEntries] = useState([]);
  const [arbeitszeitraeume, setArbeitszeitraeume] = useState([]);
  const [appSettings, setAppSettings] = useState({ company_name: '', ods_template: null, description_max_length: 40 });
  const [viewMode, setViewMode] = useState('month'); // 'month' oder 'year'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [stundeneintraege, setStundeneintraege] = useState([]);
  const [wochenberichte, setWochenberichte] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [baustellen, setBaustellen] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [showLohnexportModal, setShowLohnexportModal] = useState(false);
  const [lohnexportStep, setLohnexportStep] = useState(1);
  const [lohnexportGruppen, setLohnexportGruppen] = useState([]);
  const [selectedLohnexportGroupId, setSelectedLohnexportGroupId] = useState('');
  const [editingLohnexportGruppe, setEditingLohnexportGruppe] = useState(null);
  const [savingLohnexportGruppe, setSavingLohnexportGruppe] = useState(false);
  const [exportingLohnexport, setExportingLohnexport] = useState(false);
  const [exportingLohnexportAutomatik, setExportingLohnexportAutomatik] = useState(false);
  const [fillPayrollToMonthEnd, setFillPayrollToMonthEnd] = useState(false);
  const [lohnexportMonth, setLohnexportMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  function parseSelection(value) {
    if (!value) return null;
    const [kind, id] = String(value).split(':');
    const parsedId = parseInt(id, 10);
    if (!kind || Number.isNaN(parsedId)) return null;
    return { kind, id: parsedId };
  }

  const loadMitarbeiter = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/core/users/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setMitarbeiter(response.data.results || response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Mitarbeiter:', error);
    }
  }, [token]);

  const loadBaustellen = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/baustellen/baustellen/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setBaustellen(response.data.results || response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Baustellen:', error);
    }
  }, [token]);

  const loadSubunternehmer = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/core/subunternehmer/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSubunternehmer(response.data.results || response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Subunternehmer:', error);
    }
  }, [token]);

  const loadTagOptionen = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/stunden/optionen/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTagOptionen(response.data.results || response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Tag-Optionen:', error);
    }
  }, [token]);

  const loadArbeitszeitraeume = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/stunden/arbeitszeitraeume/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setArbeitszeitraeume(response.data.results || response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Arbeitszeiträume:', error);
    }
  }, [token]);

  const loadAppSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/core/app-settings/`);
      setAppSettings(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der App-Settings:', error);
    }
  }, []);

  const loadCalendars = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/kalender/external-calendars/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCalendars(response.data.results || response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Kalender:', error);
    }
  }, [token]);

  const loadLohnexportGruppen = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/stunden/lohnexport-gruppen/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const gruppen = response.data.results || response.data;
      setLohnexportGruppen(gruppen);
      if (gruppen.length > 0) {
        setSelectedLohnexportGroupId((prev) => {
          if (!prev) return String(gruppen[0].id);
          const exists = gruppen.some(g => String(g.id) === String(prev));
          return exists ? prev : String(gruppen[0].id);
        });
      } else {
        setSelectedLohnexportGroupId('');
      }
    } catch (error) {
      console.error('Fehler beim Laden der Lohnexport-Gruppen:', error);
      alert('Fehler beim Laden der Lohnexport-Gruppen');
    }
  }, [token]);

  const loadCalendarEvents = useCallback(async () => {
    try {
      // Lade alle Events (das Backend filtert bereits nach aktuellem User)
      const response = await axios.get(`${API_BASE_URL}/kalender/calendar-events/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const events = response.data.results || response.data;

      // Füge Kalender-Informationen zu Events hinzu
      const eventsWithCalendarInfo = events.map(event => {
        const calendar = calendars.find(c => c.id === event.calendar);
        return {
          ...event,
          calendar_color: calendar?.color || '#3b82f6',
          calendar_name: calendar?.name || 'Kalender',
          calendar_all_events_as_holiday: calendar?.all_events_as_holiday || false,
        };
      });

      setCalendarEvents(eventsWithCalendarInfo);
    } catch (error) {
      console.error('Fehler beim Laden der Kalender-Events:', error);
    }
  }, [calendars, token]);

  const loadStunden = useCallback(async () => {
    setLoading(true);
    try {
      const selection = parseSelection(selectedMitarbeiter);
      // Lade Wochenberichte für den Zeitraum
      const startDate = viewMode === 'month'
        ? new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
        : new Date(currentDate.getFullYear(), 0, 1);

      const endDate = viewMode === 'month'
        ? new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)
        : new Date(currentDate.getFullYear(), 11, 31);

      const response = await axios.get(`${API_BASE_URL}/stunden/wochenberichte/`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: selection
          ? (selection.kind === 's'
            ? { subunternehmer: selection.id }
            : { mitarbeiter: selection.id })
          : {}
      });

      const berichte = response.data.results || response.data;
      setWochenberichte(berichte);

      // Sammle alle Stundeneinträge
      const alleEintraege = [];
      berichte.forEach(bericht => {
        if (bericht.stundeneintraege) {
          bericht.stundeneintraege.forEach(eintrag => {
            alleEintraege.push({
              ...eintrag,
              status: bericht.status,
              wochenbericht_id: bericht.id
            });
          });
        }
      });

      // Filtere nach Zeitraum (timezone-sicher durch String-Vergleich)
      const startDateStr = formatDate(startDate);
      const endDateStr = formatDate(endDate);
      const gefilterteEintraege = alleEintraege.filter(e => {
        return e.datum >= startDateStr && e.datum <= endDateStr;
      });

      setStundeneintraege(gefilterteEintraege);
    } catch (error) {
      console.error('Fehler beim Laden der Stunden:', error);
    }
    setLoading(false);
  }, [currentDate, selectedMitarbeiter, token, viewMode]);

  const loadTagOptionEntries = useCallback(async () => {
    const selection = parseSelection(selectedMitarbeiter);
    if (!selection) {
      setTagOptionEntries([]);
      return;
    }
    try {
      const startDate = viewMode === 'month'
        ? new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
        : new Date(currentDate.getFullYear(), 0, 1);
      const endDate = viewMode === 'month'
        ? new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)
        : new Date(currentDate.getFullYear(), 11, 31);

      const response = await axios.get(`${API_BASE_URL}/stunden/tagoptionen/`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: {
          ...(selection.kind === 's'
            ? { subunternehmer: selection.id }
            : { mitarbeiter: selection.id }),
          von: formatDate(startDate),
          bis: formatDate(endDate)
        }
      });
      setTagOptionEntries(response.data.results || response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Tag-Optionen:', error);
    }
  }, [currentDate, selectedMitarbeiter, token, viewMode]);

  useEffect(() => {
    loadMitarbeiter();
    loadSubunternehmer();
    loadBaustellen();
    loadCalendars();
    loadTagOptionen();
    loadArbeitszeitraeume();
    loadAppSettings();
  }, [loadMitarbeiter, loadSubunternehmer, loadBaustellen, loadCalendars, loadTagOptionen, loadArbeitszeitraeume, loadAppSettings]);

  useEffect(() => {
    if (calendars.length > 0) {
      loadCalendarEvents();
    }
  }, [calendars, currentDate, viewMode, loadCalendarEvents]);

  useEffect(() => {
    if (selectedMitarbeiter) {
      loadStunden();
      loadTagOptionEntries();
    }
  }, [selectedMitarbeiter, currentDate, viewMode, loadStunden, loadTagOptionEntries]);

  async function saveEntry(entry) {
    try {
      await axios.put(`${API_BASE_URL}/stunden/eintraege/${entry.id}/`, entry, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setEditingEntry(null);
      loadStunden();
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      alert('Fehler beim Speichern!');
    }
  }

  async function deleteEntry(entry, tagOption) {
    // Sicherheitsabfrage
    const confirmed = window.confirm(
      `Möchten Sie diesen Eintrag wirklich löschen?\n\n` +
      `Datum: ${entry.datum}\n` +
      `Stunden: ${entry.stunden}h\n` +
      `Beschreibung: ${entry.beschreibung || 'Keine'}`
    );

    if (!confirmed) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/stunden/eintraege/${entry.id}/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (tagOption && tagOption.option_details && entry.baustelle == null) {
        const opt = tagOption.option_details;
        const expectedPrefix = `${opt.kurz} - ${opt.name}`;
        const desc = entry.beschreibung || '';
        const hours = parseFloat(entry.stunden || 0);
        const isOptionEntry = (hours === 8) || desc.startsWith(expectedPrefix);
        if (isOptionEntry) {
          try {
            await axios.delete(`${API_BASE_URL}/stunden/tagoptionen/${tagOption.id}/`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
          } catch (error) {
            console.error('Fehler beim Löschen der Tag-Option:', error);
          }
        }
      }
      setEditingEntry(null);
      loadStunden();
      loadTagOptionEntries();
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      alert('Fehler beim Löschen!');
    }
  }

  async function createNewEntry(date) {
    const selection = parseSelection(selectedMitarbeiter);
    if (!selection) {
      alert('Bitte wählen Sie einen Mitarbeiter oder Subunternehmer.');
      return;
    }
    if (tagOptionMode) {
      await createTagOptionEntry(date, selection);
      return;
    }

    // Finde oder erstelle Wochenbericht für dieses Datum
    const dateObj = new Date(date);
    const kw = getWeekNumber(dateObj);
    const jahr = dateObj.getFullYear();

    // Suche existierenden Wochenbericht
    let wochenbericht = wochenberichte.find(wb => wb.jahr === jahr && wb.kw === kw);

    if (!wochenbericht) {
      // Erstelle neuen Wochenbericht
      const monday = getMonday(dateObj);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);

      try {
        const response = await axios.post(`${API_BASE_URL}/stunden/wochenberichte/`, {
          ...(selection.kind === 's'
            ? { subunternehmer: selection.id }
            : { mitarbeiter: selection.id }),
          jahr: jahr,
          kw: kw,
          von_datum: formatDate(monday),
          bis_datum: formatDate(sunday),
          status: 'ENTWURF'
        }, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        wochenbericht = response.data;
      } catch (error) {
        console.error('Fehler beim Erstellen des Wochenberichts:', error);
        alert('Fehler beim Erstellen des Wochenberichts!');
        return;
      }
    }

    // Erstelle neuen Eintrag
    try {
      await axios.post(`${API_BASE_URL}/stunden/eintraege/`, {
        wochenbericht: wochenbericht.id,
        datum: formatDate(date),
        baustelle: null,
        stunden: 0,
        beschreibung: ''
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadStunden();
      loadTagOptionEntries();
    } catch (error) {
      console.error('Fehler beim Erstellen des Eintrags:', error);
      alert('Fehler beim Erstellen des Eintrags!');
    }
  }

  async function createTagOptionEntry(date, selection) {
    if (!selectedTagOption) {
      alert('Bitte wählen Sie eine Tag-Option aus.');
      return;
    }

    const option = tagOptionen.find(o => o.id === parseInt(selectedTagOption, 10));
    const dateObj = new Date(date);
    const weekdayKey = ['so', 'mo', 'di', 'mi', 'do', 'fr', 'sa'][dateObj.getDay()];
    if (option && !(option[`aktiv_${weekdayKey}`] ?? true)) {
      alert('Diese Tag-Option ist für diesen Wochentag deaktiviert.');
      return;
    }
    const kw = getWeekNumber(dateObj);
    const jahr = dateObj.getFullYear();

    let wochenbericht = wochenberichte.find(wb => wb.jahr === jahr && wb.kw === kw);

    if (!wochenbericht) {
      const monday = getMonday(dateObj);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);

      try {
        const response = await axios.post(`${API_BASE_URL}/stunden/wochenberichte/`, {
          ...(selection.kind === 's'
            ? { subunternehmer: selection.id }
            : { mitarbeiter: selection.id }),
          jahr: jahr,
          kw: kw,
          von_datum: formatDate(monday),
          bis_datum: formatDate(sunday),
          status: 'ENTWURF'
        }, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        wochenbericht = response.data;
      } catch (error) {
        console.error('Fehler beim Erstellen des Wochenberichts:', error);
        alert('Fehler beim Erstellen des Wochenberichts!');
        return;
      }
    }

    try {
      await axios.post(`${API_BASE_URL}/stunden/tagoptionen/`, {
        ...(selection.kind === 's'
          ? { subunternehmer: selection.id }
          : { mitarbeiter: selection.id }),
        tag: formatDate(date),
        option: option?.id
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadTagOptionEntries();
    } catch (error) {
      console.error('Fehler beim Speichern der Tag-Option:', error);
      alert('Fehler beim Speichern der Tag-Option!');
      return;
    }

    try {
      const beschreibung = option ? `${option.kurz} - ${option.name}` : 'Sondertag';
      const period = getArbeitszeitForDate(dateObj);
      const computedWindow = option?.arbeitszeit_automatisch_berechnen
        ? getClampedArbeitszeitWindow(option, dateObj, period)
        : null;
      const optionHours = option?.berechnungstyp === 'ARBEITSZEIT' && option?.arbeitszeit_automatisch_berechnen
        ? Number(computedWindow?.hours || 0)
        : 8;
      await axios.post(`${API_BASE_URL}/stunden/eintraege/`, {
        wochenbericht: wochenbericht.id,
        datum: formatDate(date),
        baustelle: null,
        stunden: optionHours,
        beschreibung: beschreibung
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadStunden();
    } catch (error) {
      console.error('Fehler beim Erstellen des Eintrags:', error);
      alert('Fehler beim Erstellen des Eintrags!');
    }
  }

  function getPersonNameById(id) {
    const person = mitarbeiter.find(m => Number(m.id) === Number(id));
    if (!person) return `Mitarbeiter ${id}`;
    return `${person.first_name || ''} ${person.last_name || ''}`.trim() || person.username || `Mitarbeiter ${id}`;
  }

  function openLohnexportModal() {
    setShowLohnexportModal(true);
    setLohnexportStep(1);
    setEditingLohnexportGruppe(null);
    setFillPayrollToMonthEnd(false);
    setLohnexportMonth(`${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`);
    loadLohnexportGruppen();
  }

  function closeLohnexportModal() {
    if (exportingLohnexport) return;
    setShowLohnexportModal(false);
    setLohnexportStep(1);
    setEditingLohnexportGruppe(null);
    setFillPayrollToMonthEnd(false);
  }

  function startNeueLohnexportGruppe() {
    setEditingLohnexportGruppe({ id: null, name: '', mitarbeiter: [] });
  }

  function startLohnexportGruppeBearbeiten() {
    const selected = lohnexportGruppen.find(g => String(g.id) === String(selectedLohnexportGroupId));
    if (!selected) {
      alert('Bitte zuerst eine Gruppe auswählen.');
      return;
    }
    setEditingLohnexportGruppe({
      id: selected.id,
      name: selected.name,
      mitarbeiter: (selected.mitarbeiter || []).map(id => Number(id))
    });
  }

  async function saveLohnexportGruppe() {
    if (!editingLohnexportGruppe) return;
    const name = (editingLohnexportGruppe.name || '').trim();
    if (!name) {
      alert('Bitte einen Gruppennamen eingeben.');
      return;
    }
    setSavingLohnexportGruppe(true);
    try {
      const payload = {
        name,
        mitarbeiter: (editingLohnexportGruppe.mitarbeiter || []).map(id => Number(id))
      };
      if (editingLohnexportGruppe.id) {
        await axios.put(`${API_BASE_URL}/stunden/lohnexport-gruppen/${editingLohnexportGruppe.id}/`, payload, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_BASE_URL}/stunden/lohnexport-gruppen/`, payload, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      setEditingLohnexportGruppe(null);
      await loadLohnexportGruppen();
    } catch (error) {
      console.error('Fehler beim Speichern der Lohnexport-Gruppe:', error);
      alert('Fehler beim Speichern der Lohnexport-Gruppe');
    }
    setSavingLohnexportGruppe(false);
  }

  async function deleteLohnexportGruppe() {
    const selected = lohnexportGruppen.find(g => String(g.id) === String(selectedLohnexportGroupId));
    if (!selected) {
      alert('Bitte zuerst eine Gruppe auswählen.');
      return;
    }
    if (!window.confirm(`Gruppe "${selected.name}" wirklich löschen?`)) {
      return;
    }
    try {
      await axios.delete(`${API_BASE_URL}/stunden/lohnexport-gruppen/${selected.id}/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      await loadLohnexportGruppen();
    } catch (error) {
      console.error('Fehler beim Löschen der Lohnexport-Gruppe:', error);
      alert('Fehler beim Löschen der Lohnexport-Gruppe');
    }
  }

  function addMitarbeiterToEditingGruppe(id) {
    if (!editingLohnexportGruppe) return;
    if (editingLohnexportGruppe.mitarbeiter.includes(id)) return;
    setEditingLohnexportGruppe({
      ...editingLohnexportGruppe,
      mitarbeiter: [...editingLohnexportGruppe.mitarbeiter, id]
    });
  }

  function removeMitarbeiterFromEditingGruppe(id) {
    if (!editingLohnexportGruppe) return;
    setEditingLohnexportGruppe({
      ...editingLohnexportGruppe,
      mitarbeiter: editingLohnexportGruppe.mitarbeiter.filter(mid => Number(mid) !== Number(id))
    });
  }

  function formatDate(date) {
    // Wenn bereits ein String im richtigen Format, direkt zurückgeben
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }

    // Ansonsten zu Date-Objekt konvertieren und formatieren
    const d = date instanceof Date ? date : new Date(date);

    // Verhindere Timezone-Probleme durch direkte Formatierung
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function getMonday(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  }

  function getWeekNumber(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  }

  function getDaysInMonth(date) {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    // Montag = 1, Sonntag = 0
    // Berechne, wie viele leere Tage am Anfang nötig sind (Montag als erster Tag)
    let startingDayOfWeek = firstDay.getDay();
    startingDayOfWeek = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1; // Konvertiere: Mo=0, Di=1, ..., So=6

    const days = [];
    // Füge leere Tage am Anfang hinzu
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    // Füge die Tage des Monats hinzu - WICHTIG: Direkt als Date-Objekt speichern
    // um später korrekt zu formatieren
    for (let day = 1; day <= daysInMonth; day++) {
      // Erstelle Date-Objekt explizit mit lokaler Zeitzone
      const dayDate = new Date(year, month, day, 0, 0, 0, 0);
      days.push(dayDate);
    }
    return days;
  }

  function getMonthsInYear(year) {
    return Array.from({ length: 12 }, (_, i) => new Date(year, i, 1));
  }

  function getEntriesForDate(date) {
    if (!date) return [];
    // Verhindere Timezone-Probleme durch direkte Formatierung
    const dateStr = formatDate(date);
    return stundeneintraege.filter(e => e.datum === dateStr);
  }

  function getEventsForDate(date) {
    if (!date) return [];
    // Verhindere Timezone-Probleme durch direkte Formatierung
    const dateStr = formatDate(date);

    return calendarEvents.filter(event => {
      // Wenn "Nur Startdatum verwenden" aktiv ist, nur am Startdatum anzeigen
      if (event.calendar_use_start_date_only) {
        return event.start_date === dateStr;
      }
      // WICHTIG: Gleiche Logik wie in KalenderView (umgekehrte Reihenfolge)!
      return event.start_date <= dateStr && event.end_date >= dateStr;
    });
  }

  function getHolidayInfoForDate(date) {
    if (!date) return null;
    const dateEvents = getEventsForDate(date).filter(event => event.calendar_all_events_as_holiday);
    if (dateEvents.length === 0) return null;
    return {
      label: 'Feiertag',
      events: dateEvents,
    };
  }

  function getEntriesForMonth(monthDate) {
    const year = monthDate.getFullYear();
    const month = String(monthDate.getMonth() + 1).padStart(2, '0');
    // Verhindere Timezone-Probleme durch direkten String-Vergleich
    return stundeneintraege.filter(e => {
      return e.datum.startsWith(`${year}-${month}`);
    });
  }

  function getTotalHoursForMonth(monthDate) {
    const entries = getEntriesForMonth(monthDate);
    return entries.reduce((sum, e) => sum + parseFloat(e.stunden || 0), 0);
  }

  function previousPeriod() {
    if (viewMode === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    } else {
      setCurrentDate(new Date(currentDate.getFullYear() - 1, 0, 1));
    }
  }

  function nextPeriod() {
    if (viewMode === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    } else {
      setCurrentDate(new Date(currentDate.getFullYear() + 1, 0, 1));
    }
  }

  function buildExportData() {
    const selection = parseSelection(selectedMitarbeiter);
    const entries = viewMode === 'month'
      ? stundeneintraege.filter(e => {
          // Verhindere Timezone-Probleme durch direkten String-Vergleich
          const year = currentDate.getFullYear();
          const month = String(currentDate.getMonth() + 1).padStart(2, '0');
          return e.datum.startsWith(`${year}-${month}`);
        })
      : stundeneintraege.filter(e => {
          // Verhindere Timezone-Probleme durch direkten String-Vergleich
          const year = currentDate.getFullYear();
          return e.datum.startsWith(`${year}`);
        });

    const mitarbeiterData = selection?.kind === 'm'
      ? mitarbeiter.find(m => m.id === selection.id)
      : null;
    const subunternehmerData = selection?.kind === 's'
      ? subunternehmer.find(s => s.id === selection.id)
      : null;
    const displayName = selection?.kind === 's'
      ? `${subunternehmerData?.firma || ''}${subunternehmerData?.ansprechpartner ? `: ${subunternehmerData.ansprechpartner}` : ''}`.trim()
      : `${mitarbeiterData?.first_name || ''} ${mitarbeiterData?.last_name || ''}`.trim();
    const period = viewMode === 'month'
      ? currentDate.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })
      : currentDate.getFullYear();

    const sortedEntries = [...entries].sort((a, b) => {
      const dateCompare = String(a.datum).localeCompare(String(b.datum));
      if (dateCompare !== 0) return dateCompare;
      return (a.id || 0) - (b.id || 0);
    });

    const totalHours = sortedEntries.reduce((sum, e) => sum + parseFloat(e.stunden || 0), 0);
    const lines = [];
    const rows = [];
    let emailDate = null;
    let daySum = 0;
    let overtimeTotal = 0;

    rows.push([displayName]);
    rows.push([`Zeitraum: ${period}`]);
    rows.push([]);
    rows.push(['Datum', 'Dauer', 'Baustelle', 'Beschreibung']);

    sortedEntries.forEach((e, index) => {
      if (emailDate && e.datum !== emailDate) {
        const overtime = Math.max(0, daySum - 8);
        overtimeTotal += overtime;
        lines.push(`Summe ${emailDate}: ${daySum}h${overtime > 0 ? ` (Überstunden: ${overtime}h)` : ''}`);
        lines.push('');
        rows.push([`Summe ${emailDate}`, `${daySum}h`, overtime > 0 ? `Überstunden: ${overtime}h` : '', '']);
        rows.push([]);
        daySum = 0;
      }

      emailDate = e.datum;
      const baustelle = baustellen.find(b => b.id === e.baustelle);
      const baustelleText = getBaustelleDisplayName(baustelle);
      lines.push(`${e.datum} | ${e.stunden}h | ${baustelleText} | ${e.beschreibung || '-'}`);
      daySum += parseFloat(e.stunden || 0);
      rows.push([e.datum, `${e.stunden}h`, baustelleText, e.beschreibung || '-']);

      if (index === sortedEntries.length - 1 && emailDate) {
        const overtime = Math.max(0, daySum - 8);
        overtimeTotal += overtime;
        lines.push(`Summe ${emailDate}: ${daySum}h${overtime > 0 ? ` (Überstunden: ${overtime}h)` : ''}`);
        rows.push([`Summe ${emailDate}`, `${daySum}h`, overtime > 0 ? `Überstunden: ${overtime}h` : '', '']);
      }
    });

    rows.push([]);
    rows.push(['Summe', `${totalHours}h`, '', '']);
    rows.push(['Überstunden', `${overtimeTotal}h`, '', '']);

    return {
      mitarbeiterData,
      subunternehmerData,
      displayName,
      period,
      lines,
      totalHours,
      overtimeTotal,
      rows
    };
  }

  function exportAsEmail() {
    const { displayName, period, lines, totalHours, overtimeTotal } = buildExportData();
    const body = `${displayName}` + '\n' +
      `Zeitraum: ${period}\n\n` +
      lines.join('\n') + '\n\n' +
      `Summe: ${totalHours}h\n` +
      `Überstunden: ${overtimeTotal}h`;

    const subject = `Stundenübersicht ${displayName} - ${period}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  function escapeXml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }

  function buildOdsContent(rows, sheetName) {
    const safeSheetName = escapeXml(sheetName);
    const rowXml = rows.map(row => {
      const cells = (row || []).map(cell => (
        `<table:table-cell office:value-type="string"><text:p>${escapeXml(cell ?? '')}</text:p></table:table-cell>`
      )).join('');
      return `<table:table-row>${cells}</table:table-row>`;
    }).join('');

    return `<?xml version="1.0" encoding="UTF-8"?>\n` +
      `<office:document-content\n` +
      ` xmlns:office="urn:oasis:names:tc:opendocument:xmlns:office:1.0"\n` +
      ` xmlns:table="urn:oasis:names:tc:opendocument:xmlns:table:1.0"\n` +
      ` xmlns:text="urn:oasis:names:tc:opendocument:xmlns:text:1.0"\n` +
      ` office:version="1.2">\n` +
      ` <office:body>\n` +
      `  <office:spreadsheet>\n` +
      `   <table:table table:name="${safeSheetName}">\n` +
      `    ${rowXml}\n` +
      `   </table:table>\n` +
      `  </office:spreadsheet>\n` +
      ` </office:body>\n` +
      `</office:document-content>\n`;
  }

  function downloadOds(filename, rows, sheetName) {
    const contentXml = buildOdsContent(rows, sheetName);
    const metaXml = `<?xml version="1.0" encoding="UTF-8"?>\n` +
      `<office:document-meta xmlns:office="urn:oasis:names:tc:opendocument:xmlns:office:1.0" office:version="1.2">\n` +
      ` <office:meta />\n` +
      `</office:document-meta>\n`;
    const stylesXml = `<?xml version="1.0" encoding="UTF-8"?>\n` +
      `<office:document-styles xmlns:office="urn:oasis:names:tc:opendocument:xmlns:office:1.0" office:version="1.2">\n` +
      ` <office:styles />\n` +
      `</office:document-styles>\n`;
    const settingsXml = `<?xml version="1.0" encoding="UTF-8"?>\n` +
      `<office:document-settings xmlns:office="urn:oasis:names:tc:opendocument:xmlns:office:1.0" office:version="1.2">\n` +
      ` <office:settings />\n` +
      `</office:document-settings>\n`;
    const manifestXml = `<?xml version="1.0" encoding="UTF-8"?>\n` +
      `<manifest:manifest xmlns:manifest="urn:oasis:names:tc:opendocument:xmlns:manifest:1.0">\n` +
      ` <manifest:file-entry manifest:media-type="application/vnd.oasis.opendocument.spreadsheet" manifest:full-path="/" />\n` +
      ` <manifest:file-entry manifest:media-type="text/xml" manifest:full-path="content.xml" />\n` +
      ` <manifest:file-entry manifest:media-type="text/xml" manifest:full-path="meta.xml" />\n` +
      ` <manifest:file-entry manifest:media-type="text/xml" manifest:full-path="styles.xml" />\n` +
      ` <manifest:file-entry manifest:media-type="text/xml" manifest:full-path="settings.xml" />\n` +
      `</manifest:manifest>\n`;

    const zipData = zipSync({
      'mimetype': [strToU8('application/vnd.oasis.opendocument.spreadsheet'), { level: 0 }],
      'content.xml': strToU8(contentXml),
      'meta.xml': strToU8(metaXml),
      'styles.xml': strToU8(stylesXml),
      'settings.xml': strToU8(settingsXml),
      'META-INF/manifest.xml': strToU8(manifestXml)
    }, { level: 6 });

    const blob = new Blob([zipData], { type: 'application/vnd.oasis.opendocument.spreadsheet' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
  }

  function exportAsExcel() {
    const { displayName, period, rows } = buildExportData();
    const safeName = `${displayName}`.trim().replace(/\s+/g, '_') || 'Mitarbeiter';
    const safePeriod = String(period).replace(/\s+/g, '_');
    downloadOds(`Stundenuebersicht_${safeName}_${safePeriod}.ods`, rows, 'Stundenuebersicht');
  }

  function exportPayroll() {
    if (viewMode !== 'month') {
      alert('Der Lohnbuchhaltung Export ist nur in der Monatsansicht verfuegbar.');
      return;
    }
    const { displayName, period } = buildExportData();
    const selection = parseSelection(selectedMitarbeiter);
    if (!selection) {
      alert('Bitte wählen Sie eine Person.');
      return;
    }
    if (!appSettings?.ods_template) {
      alert('Bitte eine ODS Vorlage unter Einstellungen → Branding hochladen.');
      return;
    }
    exportPayrollWithTemplate(displayName, period);
  }

  function sanitizeSheetName(name, index) {
    const cleaned = String(name || '')
      .replace(/[\\/:*?\[\]]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const fallback = `Mitarbeiter_${index + 1}`;
    const base = cleaned || fallback;
    return base.length > 28 ? base.slice(0, 28) : base;
  }

  function renderPayrollTable(tableTemplateXml, displayName, rows, sheetName, exportDate) {
    let tableXml = tableTemplateXml;
    tableXml = tableXml.replace(/table:name="[^"]*"/, `table:name="${escapeXml(sheetName)}"`);
    const effectiveDate = exportDate || currentDate;

    const headerReplacements = {
      Firmenname: appSettings?.company_name || '',
      MonatsnummerMM: String(effectiveDate.getMonth() + 1).padStart(2, '0'),
      JahrJJJJ: String(effectiveDate.getFullYear()),
      name: displayName
    };

    Object.entries(headerReplacements).forEach(([key, value]) => {
      tableXml = tableXml.replaceAll(`{{${key}}}`, value);
    });

    const rowRegex = /<table:table-row[^>]*>[\s\S]*?<\/table:table-row>/g;
    const rowsFound = Array.from(tableXml.matchAll(rowRegex));
    if (rowsFound.length < 8) {
      throw new Error('Vorlage: Zeile 8 nicht gefunden.');
    }
    const rowTemplate = rowsFound[7][0];
    const rowIndex = rowsFound[7].index;
    if (rowIndex == null) {
      throw new Error('Vorlage: Zeile 8 Position nicht gefunden.');
    }

    const renderedRows = rows.map(r => {
      let row = rowTemplate;
      row = row.replaceAll('{{KalenderwocheWW}}', r.KalenderwocheWW);
      row = row.replaceAll('{{TagImMonatTT}}', r.TagImMonatTT);
      row = row.replaceAll('{{WochentagTT}}', r.WochentagTT);
      row = row.replaceAll('{{Arbeitsstunden}}', r.Arbeitsstunden);
      row = row.replaceAll('{{Tagesoption}}', r.Tagesoption);
      row = row.replaceAll('{{Ausfallstunden}}', r.Ausfallstunden);
      row = row.replaceAll('{{ArbeitsbeginnHH:MM}}', r.Arbeitsbeginn);
      row = row.replaceAll('{{ArbeitsendeHH:MM}}', r.Arbeitsende);
      row = row.replaceAll('{{PauseHH:MM}}', r.Pause);
      row = row.replaceAll('{{Bemerkungen}}', r.Bemerkungen);
      return row;
    }).join('');

    return tableXml.slice(0, rowIndex) + renderedRows + tableXml.slice(rowIndex + rowTemplate.length);
  }

  function extractMonthEntriesFromBerichte(berichte, startDateStr, endDateStr) {
    const alleEintraege = [];
    berichte.forEach((bericht) => {
      if (!bericht.stundeneintraege) return;
      bericht.stundeneintraege.forEach((eintrag) => {
        alleEintraege.push({
          ...eintrag,
          status: bericht.status,
          wochenbericht_id: bericht.id
        });
      });
    });
    return alleEintraege.filter(e => e.datum >= startDateStr && e.datum <= endDateStr);
  }

  function parseExportMonth(value) {
    const parsed = /^(\d{4})-(\d{2})$/.exec(String(value || ''));
    if (!parsed) return null;
    const year = Number(parsed[1]);
    const month = Number(parsed[2]);
    if (Number.isNaN(year) || Number.isNaN(month) || month < 1 || month > 12) return null;
    return new Date(year, month - 1, 1);
  }

  function formatPeriodLabel(date) {
    return date.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
  }

  async function fetchAllResults(url, params = {}) {
    const headers = { 'Authorization': `Bearer ${token}` };
    let nextUrl = url;
    let nextParams = params;
    const all = [];

    while (nextUrl) {
      const response = await axios.get(nextUrl, {
        headers,
        params: nextParams
      });
      const data = response.data;

      if (Array.isArray(data)) {
        return data;
      }
      if (!Array.isArray(data?.results)) {
        return [];
      }

      all.push(...data.results);
      if (data.next) {
        nextUrl = data.next;
        nextParams = undefined;
      } else {
        nextUrl = null;
      }
    }

    return all;
  }

  async function fetchMonthDataForMitarbeiter(mitarbeiterId, targetDate) {
    const startDate = new Date(targetDate.getFullYear(), targetDate.getMonth(), 1);
    const endDate = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0);
    const startDateStr = formatDate(startDate);
    const endDateStr = formatDate(endDate);

    const [berichte, tagOptions] = await Promise.all([
      fetchAllResults(`${API_BASE_URL}/stunden/wochenberichte/`, { mitarbeiter: mitarbeiterId }),
      fetchAllResults(`${API_BASE_URL}/stunden/tagoptionen/`, { mitarbeiter: mitarbeiterId, von: startDateStr, bis: endDateStr })
    ]);
    const entries = extractMonthEntriesFromBerichte(berichte, startDateStr, endDateStr);
    return { entries, tagOptions };
  }

  async function exportPayrollForMemberIds(memberIds, exportDate, filenamePrefix, options = {}) {
    const response = await axios.get(appSettings.ods_template, { responseType: 'arraybuffer' });
    const templateData = new Uint8Array(response.data);
    const zipped = unzipSync(templateData);
    const contentXml = strFromU8(zipped['content.xml']);
    const tableMatch = contentXml.match(/<table:table\b[\s\S]*?<\/table:table>/);
    if (!tableMatch || tableMatch.index == null) {
      throw new Error('Vorlage: Tabellenblatt nicht gefunden.');
    }

    const baseTableXml = tableMatch[0];
    const sheetXmlList = [];
    const period = formatPeriodLabel(exportDate);

    for (let i = 0; i < memberIds.length; i += 1) {
      const id = memberIds[i];
      const displayName = getPersonNameById(id);
      const monthData = await fetchMonthDataForMitarbeiter(id, exportDate);
      const rows = buildPayrollRows(monthData.entries, monthData.tagOptions, exportDate, options);
      const sheetName = sanitizeSheetName(displayName, i);
      const renderedTable = renderPayrollTable(baseTableXml, displayName, rows, sheetName, exportDate);
      sheetXmlList.push(renderedTable);
    }

    const start = tableMatch.index;
    const end = start + baseTableXml.length;
    const newContent = contentXml.slice(0, start) + sheetXmlList.join('') + contentXml.slice(end);
    zipped['content.xml'] = strToU8(newContent);

    const out = zipSync(zipped, { level: 6 });
    const blob = new Blob([out], { type: 'application/vnd.oasis.opendocument.spreadsheet' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    const safePeriod = String(period).replace(/\s+/g, '_');
    link.download = `${filenamePrefix}_${safePeriod}.ods`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
  }

  async function exportPayrollForGruppe(gruppe) {
    if (!gruppe) {
      alert('Bitte eine Gruppe auswählen.');
      return;
    }
    if (!appSettings?.ods_template) {
      alert('Bitte eine ODS Vorlage unter Einstellungen → Branding hochladen.');
      return;
    }
    const memberIds = (gruppe.mitarbeiter || []).map(id => Number(id));
    if (memberIds.length === 0) {
      alert('Die gewählte Gruppe enthält keine Mitarbeiter.');
      return;
    }
    const exportDate = parseExportMonth(lohnexportMonth);
    if (!exportDate) {
      alert('Bitte einen gültigen Monat auswählen.');
      return;
    }

    setExportingLohnexport(true);
    try {
      const safeGroupName = (gruppe.name || 'Lohnexport').replace(/\s+/g, '_');
      await exportPayrollForMemberIds(memberIds, exportDate, `Lohnexporte_${safeGroupName}`, {
        fillToMonthEndWithRegularHours: fillPayrollToMonthEnd
      });
      setShowLohnexportModal(false);
      setLohnexportStep(1);
    } catch (error) {
      console.error('Fehler beim Gruppenenxport:', error);
      alert('Fehler beim Lohnexport der Gruppe.');
    }
    setExportingLohnexport(false);
  }

  async function exportPayrollLetzterMonatAutomatik() {
    if (!appSettings?.ods_template) {
      alert('Bitte eine ODS Vorlage unter Einstellungen → Branding hochladen.');
      return;
    }
    const now = new Date();
    const exportDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const startDateStr = formatDate(new Date(exportDate.getFullYear(), exportDate.getMonth(), 1));
    const endDateStr = formatDate(new Date(exportDate.getFullYear(), exportDate.getMonth() + 1, 0));

    setExportingLohnexportAutomatik(true);
    try {
      const berichte = await fetchAllResults(`${API_BASE_URL}/stunden/wochenberichte/`);
      const idsSet = new Set();
      berichte.forEach((bericht) => {
        if (!bericht?.mitarbeiter) return;
        const entries = bericht.stundeneintraege || [];
        const hasEntryInRange = entries.some(e => e.datum >= startDateStr && e.datum <= endDateStr);
        if (hasEntryInRange) {
          idsSet.add(Number(bericht.mitarbeiter));
        }
      });

      const memberIds = Array.from(idsSet);
      if (memberIds.length === 0) {
        alert('Im letzten Monat wurden keine Mitarbeiter mit Einträgen gefunden.');
        return;
      }
      memberIds.sort((a, b) => getPersonNameById(a).localeCompare(getPersonNameById(b), 'de', { sensitivity: 'base' }));

      await exportPayrollForMemberIds(memberIds, exportDate, 'Lohnexporte_Letzter_Monat_Automatik');
    } catch (error) {
      console.error('Fehler beim automatischen Lohnexport:', error);
      alert('Fehler beim automatischen Lohnexport des letzten Monats.');
    }
    setExportingLohnexportAutomatik(false);
  }

  function getDateKey(date) {
    return formatDate(date);
  }

  function getWeekdayKey(date) {
    return ['so', 'mo', 'di', 'mi', 'do', 'fr', 'sa'][date.getDay()];
  }

  function parseTimeToMinutes(value) {
    const match = /^(\d{2}):(\d{2})$/.exec(String(value || '').trim());
    if (!match) return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
    return (hours * 60) + minutes;
  }

  function formatMinutesToTime(totalMinutes) {
    if (!Number.isFinite(totalMinutes)) return '';
    const safeMinutes = Math.max(0, Math.round(totalMinutes));
    const hours = String(Math.floor(safeMinutes / 60)).padStart(2, '0');
    const minutes = String(safeMinutes % 60).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  function getTagOptionConfigForDate(option, dateObj) {
    if (!option || !dateObj) return null;
    const weekdayKey = getWeekdayKey(dateObj);
    return {
      active: option[`aktiv_${weekdayKey}`] ?? true,
      start: option[`beginn_${weekdayKey}`] || '',
      end: option[`ende_${weekdayKey}`] || '',
      pause: option[`pause_${weekdayKey}`] || '',
    };
  }

  function getClampedArbeitszeitWindow(option, dateObj, period) {
    const config = getTagOptionConfigForDate(option, dateObj);
    if (!config?.active) {
      return { active: false, start: '', end: '', hours: 0 };
    }

    const optionStart = parseTimeToMinutes(config.start);
    const optionEnd = parseTimeToMinutes(config.end);
    const periodStart = parseTimeToMinutes(dateObj.getDay() === 5 ? period.beginn_fr : period.beginn_mo_do);
    const periodEnd = parseTimeToMinutes(dateObj.getDay() === 5 ? period.ende_fr : period.ende_mo_do);
    const pauseMinutes = parseTimeToMinutes(config.pause) || 0;

    if (optionStart == null || optionEnd == null || periodStart == null || periodEnd == null) {
      return { active: true, start: '', end: '', hours: 0 };
    }

    const clampedStart = Math.max(optionStart, periodStart);
    const clampedEnd = Math.min(optionEnd, periodEnd);
    if (clampedEnd <= clampedStart) {
      return { active: true, start: '', end: '', hours: 0 };
    }

    const rawMinutes = Math.max(0, clampedEnd - clampedStart - pauseMinutes);
    return {
      active: true,
      start: formatMinutesToTime(clampedStart),
      end: formatMinutesToTime(clampedEnd),
      hours: rawMinutes / 60,
    };
  }

  function getWeekdayShort(date) {
    const map = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    return map[date.getDay()];
  }

  function getArbeitszeitForDate(date) {
    const year = date.getFullYear();
    const currentKey = ((date.getMonth() + 1) * 100) + date.getDate();

    function clampDay(month, day) {
      const maxDay = new Date(year, month, 0).getDate();
      return Math.max(1, Math.min(Number(day) || 1, maxDay));
    }

    for (const z of arbeitszeitraeume) {
      const startKey = (Number(z.von_monat) * 100) + clampDay(Number(z.von_monat), Number(z.von_tag));
      const endKey = (Number(z.bis_monat) * 100) + clampDay(Number(z.bis_monat), Number(z.bis_tag));
      let inRange = false;
      if (endKey >= startKey) {
        inRange = currentKey >= startKey && currentKey <= endKey;
      } else {
        inRange = currentKey >= startKey || currentKey <= endKey;
      }
      if (inRange) {
        return z;
      }
    }
    return {
      max_mo_do: 8,
      max_fr: 6,
      beginn_mo_do: '07:00',
      ende_mo_do: '16:00',
      pause_mo_do: '00:30',
      beginn_fr: '07:00',
      ende_fr: '13:00',
      pause_fr: '00:30'
    };
  }

  function buildPayrollRows(personEntries = stundeneintraege, personTagOptionEntries = tagOptionEntries, targetDate = currentDate, options = {}) {
    const year = targetDate.getFullYear();
    const month = targetDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const rows = [];
    const fillToMonthEndWithRegularHours = !!options.fillToMonthEndWithRegularHours;
    const relevantActivityDays = new Set();
    const feiertagOption = tagOptionen.find((option) => String(option.kurz || '').trim().toUpperCase() === 'F');

    personEntries.forEach((entry) => {
      if (entry?.datum) relevantActivityDays.add(entry.datum);
    });
    personTagOptionEntries.forEach((entry) => {
      if (entry?.tag) relevantActivityDays.add(entry.tag);
    });

    const latestActivityDateStr = Array.from(relevantActivityDays)
      .filter((dateStr) => typeof dateStr === 'string' && dateStr.startsWith(`${year}-${String(month + 1).padStart(2, '0')}-`))
      .sort()
      .at(-1) || null;

    for (let day = 1; day <= daysInMonth; day += 1) {
      const dateObj = new Date(year, month, day);
      const weekday = dateObj.getDay();
      const dateStr = getDateKey(dateObj);
      const period = getArbeitszeitForDate(dateObj);
      const isFriday = weekday === 5;
      const isWeekend = weekday === 0 || weekday === 6;
      const maxHours = isWeekend ? 0 : (isFriday ? parseFloat(period.max_fr) : parseFloat(period.max_mo_do));

      const dayEntries = personEntries.filter(e => e.datum === dateStr);
      const normalHours = dayEntries
        .filter(e => e.baustelle != null)
        .reduce((sum, e) => sum + parseFloat(e.stunden || 0), 0);
      const calendarHolidayInfo = getHolidayInfoForDate(dateObj);
      const dayOptionEntries = personTagOptionEntries.filter(e => e.tag === dateStr);
      const resolvedOptions = dayOptionEntries.map((entry) => {
        const opt = tagOptionen.find(o => Number(o.id) === Number(entry.option)) || entry.option_details || null;
        const kurz = opt?.kurz || '';
        const name = opt?.name || '';
        const prefix = kurz && name ? `${kurz} - ${name}` : '';
        const type = String(opt?.berechnungstyp || 'FEHLZEIT').trim().toUpperCase();
        const config = getTagOptionConfigForDate(opt, dateObj);
        return { kurz, name, prefix, type, config };
      });
      const activeResolvedOptions = resolvedOptions.filter(opt => opt.config?.active ?? true);
      const matchedOption = activeResolvedOptions.find(opt =>
        opt.prefix && dayEntries.some(e => e.baustelle == null && (e.beschreibung || '').startsWith(opt.prefix))
      ) || activeResolvedOptions[0] || null;
      const effectiveOption = matchedOption || (
        calendarHolidayInfo ? {
          kurz: feiertagOption?.kurz || 'F',
          name: feiertagOption?.name || 'Feiertag',
          prefix: feiertagOption ? `${feiertagOption.kurz} - ${feiertagOption.name}` : 'F - Feiertag',
          type: 'FEHLZEIT',
          config: { active: true },
        } : null
      );

      const optionType = effectiveOption?.type || 'FEHLZEIT';
      const optionKurz = effectiveOption?.kurz || '';
      const optionPrefix = effectiveOption?.prefix || '';
      const optionHoursFromEntries = dayEntries
        .filter(e => e.baustelle == null && (optionPrefix ? (e.beschreibung || '').startsWith(optionPrefix) : false))
        .reduce((sum, e) => sum + parseFloat(e.stunden || 0), 0);
      const optionHours = optionType === 'ARBEITSZEIT' ? optionHoursFromEntries : 0;
      const effectiveHours = normalHours + (optionType === 'ARBEITSZEIT' ? optionHours : 0);
      const cappedHours = maxHours > 0 ? Math.min(maxHours, effectiveHours) : 0;

      const hasFehlzeitOption = optionType === 'FEHLZEIT' && !!effectiveOption;
      const ausfallsymbol = hasFehlzeitOption ? optionKurz : '';
      const ausfall = (hasFehlzeitOption && normalHours > 0 && maxHours > 0 && normalHours < maxHours)
        ? (maxHours - normalHours)
        : '';

      const shouldFillWithRegularHours = (
        fillToMonthEndWithRegularHours &&
        !isWeekend &&
        !!latestActivityDateStr &&
        dateStr > latestActivityDateStr &&
        normalHours <= 0 &&
        !effectiveOption
      );

      const arbeitsstunden = (effectiveHours > 0 && maxHours > 0)
        ? cappedHours
        : (shouldFillWithRegularHours ? maxHours : '');

      const regularTimes = isFriday
        ? { beginn: period.beginn_fr, ende: period.ende_fr, pause: period.pause_fr }
        : { beginn: period.beginn_mo_do, ende: period.ende_mo_do, pause: period.pause_mo_do };
      const shouldShowTimes = shouldFillWithRegularHours || (!isWeekend && effectiveHours > 0 && !hasFehlzeitOption);
      const zeiten = !shouldShowTimes ? { beginn: '', ende: '', pause: '' } : regularTimes;

      rows.push({
        KalenderwocheWW: String(getWeekNumber(dateObj)).padStart(2, '0'),
        TagImMonatTT: String(day).padStart(2, '0'),
        WochentagTT: getWeekdayShort(dateObj),
        Arbeitsstunden: arbeitsstunden === '' ? '' : `${arbeitsstunden}`,
        Tagesoption: ausfallsymbol,
        Ausfallstunden: ausfall === '' ? '' : `${ausfall}`,
        Arbeitsbeginn: zeiten.beginn,
        Arbeitsende: zeiten.ende,
        Pause: zeiten.pause,
        Bemerkungen: ''
      });
    }

    return rows;
  }

  async function exportPayrollWithTemplate(displayName, periodLabel) {
    try {
      const response = await axios.get(appSettings.ods_template, { responseType: 'arraybuffer' });
      const templateData = new Uint8Array(response.data);
      const zipped = unzipSync(templateData);
      const contentXml = strFromU8(zipped['content.xml']);
      const tableMatch = contentXml.match(/<table:table\b[\s\S]*?<\/table:table>/);
      if (!tableMatch || tableMatch.index == null) {
        alert('Vorlage: Tabellenblatt nicht gefunden.');
        return;
      }
      const tableXml = tableMatch[0];
      const renderedTable = renderPayrollTable(
        tableXml,
        displayName,
        buildPayrollRows(),
        sanitizeSheetName(displayName, 0),
        currentDate
      );
      const tableStart = tableMatch.index;
      const tableEnd = tableStart + tableXml.length;
      const newContent = contentXml.slice(0, tableStart) + renderedTable + contentXml.slice(tableEnd);
      zipped['content.xml'] = strToU8(newContent);

      const out = zipSync(zipped, { level: 6 });
      const blob = new Blob([out], { type: 'application/vnd.oasis.opendocument.spreadsheet' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const safeName = `${displayName}`.trim().replace(/\\s+/g, '_') || 'Lohnbuchhaltung';
      const safePeriod = String(periodLabel).replace(/\\s+/g, '_');
      link.download = `Lohnbuchhaltung_${safeName}_${safePeriod}.ods`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Fehler beim Exportieren der Vorlage:', error);
      alert('Fehler beim Exportieren der Vorlage.');
    }
  }

  const tagOptionByDate = tagOptionEntries.reduce((acc, entry) => {
    if (!entry?.tag) return acc;
    const hasMatchingEntry = stundeneintraege.some(se => se.datum === entry.tag && isOptionEntryForTag(se, entry));
    if (!hasMatchingEntry) return acc;
    if (!acc[entry.tag]) {
      acc[entry.tag] = entry;
    }
    return acc;
  }, {});

  function getTagOptionForDate(date) {
    if (!date) return null;
    const dateStr = formatDate(date);
    return tagOptionByDate[dateStr] || null;
  }

  const personButtons = [
    ...mitarbeiter.map((m) => ({
      key: `m-${m.id}`,
      value: `m:${m.id}`,
      label: `${m.first_name || ''} ${m.last_name || ''}`.trim(),
    })),
    ...subunternehmer.map((s) => ({
      key: `s-${s.id}`,
      value: `s:${s.id}`,
      label: `${s.firma || ''}: ${s.ansprechpartner || ''}`.trim(),
    })),
  ]
    .filter((p) => p.label)
    .sort((a, b) => a.label.localeCompare(b.label, 'de', { sensitivity: 'base' }));

  const sortedMitarbeiter = [...mitarbeiter].sort((a, b) => (
    compareByLabelDe(`${a.last_name || ''} ${a.first_name || ''}`.trim(), `${b.last_name || ''} ${b.first_name || ''}`.trim())
  ));
  const sortedSubunternehmer = [...subunternehmer].sort((a, b) => (
    compareByLabelDe(`${a.firma || ''} ${a.ansprechpartner || ''}`.trim(), `${b.firma || ''} ${b.ansprechpartner || ''}`.trim())
  ));

  const mitarbeiterForGruppen = [...mitarbeiter]
    .filter((m) => m.role !== 'SUB')
    .sort((a, b) => {
      const an = `${a.last_name || ''} ${a.first_name || ''}`.trim();
      const bn = `${b.last_name || ''} ${b.first_name || ''}`.trim();
      return an.localeCompare(bn, 'de', { sensitivity: 'base' });
    });
  const selectedLohnexportGruppe = lohnexportGruppen.find(g => String(g.id) === String(selectedLohnexportGroupId)) || null;
  const editingMemberIds = editingLohnexportGruppe?.mitarbeiter || [];
  const availableMitarbeiterForEditing = mitarbeiterForGruppen.filter(m => !editingMemberIds.includes(Number(m.id)));
  const selectedMitarbeiterForEditing = mitarbeiterForGruppen.filter(m => editingMemberIds.includes(Number(m.id)));
  const currentMonthHours = stundeneintraege
    .filter((e) => {
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      return String(e.datum || '').startsWith(`${year}-${month}`);
    })
    .reduce((sum, e) => sum + parseFloat(e.stunden || 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>
            Stundenerfassung
          </h2>
          {selectedMitarbeiter && (
            <button
              onClick={() => setSelectedMitarbeiter(null)}
              className="btn"
              title="Zur Personenauswahl"
            >
              Zur Auswahl
            </button>
          )}
        </div>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          {/* Export-Buttons */}
          {selectedMitarbeiter && (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={exportAsEmail} className="btn" title="Als Email versenden">
                <Mail size={18} />
              </button>
              <button
                onClick={() => setTagOptionMode(!tagOptionMode)}
                className="btn"
                style={{
                  background: tagOptionMode ? '#16a34a' : undefined,
                  color: tagOptionMode ? 'white' : undefined,
                  borderColor: tagOptionMode ? '#16a34a' : undefined
                }}
                title="Option eintragen"
              >
                Option eintragen
              </button>
              <select
                value={selectedTagOption}
                onChange={(e) => setSelectedTagOption(e.target.value)}
                className="input"
                style={{ width: '200px' }}
              >
                <option value="">Tag-Option wählen...</option>
                {tagOptionen.filter(o => o.aktiv).map(o => (
                  <option key={o.id} value={o.id}>
                    {o.name} ({o.kurz})
                  </option>
                ))}
              </select>
              <button onClick={exportAsExcel} className="btn" title="Als Excel exportieren">
                <FileSpreadsheet size={18} />
              </button>
              <button onClick={exportPayroll} className="btn" title="Lohnbuchhaltung Export">
                Lohnbuchhaltung Export
              </button>
            </div>
          )}

          {/* Mitarbeiter-Auswahl */}
          <select
            value={selectedMitarbeiter || ''}
            onChange={(e) => setSelectedMitarbeiter(e.target.value)}
            className="input"
            style={{ width: '250px' }}
          >
            <option value="">Person wählen...</option>
            <option value="__ma" disabled>Mitarbeiter:</option>
            {sortedMitarbeiter.map(m => (
              <option key={`m-${m.id}`} value={`m:${m.id}`}>
                {m.first_name} {m.last_name}
              </option>
            ))}
            {sortedSubunternehmer.length > 0 && (
              <option value="__sub" disabled>Subunternehmer:</option>
            )}
            {sortedSubunternehmer.map(s => (
              <option key={`s-${s.id}`} value={`s:${s.id}`}>
                {s.firma}: {s.ansprechpartner}
              </option>
            ))}
          </select>

          {/* Ansichts-Umschalter */}
          <div style={{ display: 'flex', gap: '0.5rem', background: 'white', borderRadius: '0.5rem', padding: '0.25rem' }}>
            <button
              onClick={() => setViewMode('month')}
              className={`btn ${viewMode === 'month' ? 'btn-primary' : ''}`}
              style={{ padding: '0.5rem 1rem' }}
            >
              Monat
            </button>
            <button
              onClick={() => setViewMode('year')}
              className={`btn ${viewMode === 'year' ? 'btn-primary' : ''}`}
              style={{ padding: '0.5rem 1rem' }}
            >
              Jahr
            </button>
          </div>
        </div>
      </div>

      {!selectedMitarbeiter ? (
        <div className="card" style={{ padding: '2rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <CalendarIcon size={48} style={{ opacity: 0.35, margin: '0 auto 0.75rem' }} />
            <p style={{ color: '#6b7280', fontSize: '1rem', margin: 0 }}>
              Bitte wählen Sie einen Mitarbeiter oder Subunternehmer aus
            </p>
            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={openLohnexportModal} className="btn btn-primary">
                Lohnexporte
              </button>
              <button
                onClick={exportPayrollLetzterMonatAutomatik}
                className="btn btn-primary"
                disabled={exportingLohnexportAutomatik}
              >
                {exportingLohnexportAutomatik ? 'Läuft...' : 'Lohnexporte (Letzter Monat/Automatik)'}
              </button>
            </div>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
              gap: '0.5rem',
            }}
          >
            {personButtons.map((person) => (
              <button
                key={person.key}
                onClick={() => setSelectedMitarbeiter(person.value)}
                className="btn"
                style={{
                  width: '100%',
                  justifyContent: 'flex-start',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={person.label}
              >
                {person.label}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* Periode-Navigation */}
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button onClick={previousPeriod} className="btn">
                <ChevronLeft size={20} />
              </button>

              <div style={{ textAlign: 'center' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>
                  {viewMode === 'month'
                    ? currentDate.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })
                    : currentDate.getFullYear()
                  }
                </h3>
                <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.2rem' }}>
                  Summe aktueller Monat: {currentMonthHours.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}h
                </div>
              </div>

              <button onClick={nextPeriod} className="btn">
                <ChevronRight size={20} />
              </button>
            </div>
          </div>

          {loading ? (
            <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
              <p>Lade Daten...</p>
            </div>
          ) : viewMode === 'month' ? (
            <MonthView
              days={getDaysInMonth(currentDate)}
              getEntriesForDate={getEntriesForDate}
              getEventsForDate={getEventsForDate}
              getTagOptionForDate={getTagOptionForDate}
              getHolidayInfoForDate={getHolidayInfoForDate}
              descriptionMaxLength={Math.max(1, parseInt(appSettings?.description_max_length, 10) || 40)}
              editingEntry={editingEntry}
              setEditingEntry={setEditingEntry}
              saveEntry={saveEntry}
              deleteEntry={deleteEntry}
              createNewEntry={createNewEntry}
              baustellen={baustellen}
            />
          ) : (
            <YearView
              months={getMonthsInYear(currentDate.getFullYear())}
              getEntriesForMonth={getEntriesForMonth}
              getTotalHoursForMonth={getTotalHoursForMonth}
              setCurrentDate={setCurrentDate}
              setViewMode={setViewMode}
            />
          )}
        </>
      )}

      {showLohnexportModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div className="card" style={{ width: '100%', maxWidth: '1100px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Lohnexporte</h3>
              <button onClick={closeLohnexportModal} className="btn" disabled={exportingLohnexport}>
                <X size={16} /> Schließen
              </button>
            </div>

            {lohnexportStep === 1 ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Gespeicherte Gruppen</label>
                    <select
                      value={selectedLohnexportGroupId}
                      onChange={(e) => setSelectedLohnexportGroupId(e.target.value)}
                      className="input"
                      size={10}
                      style={{ width: '100%' }}
                    >
                      {lohnexportGruppen.map((g) => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                      <button onClick={startNeueLohnexportGruppe} className="btn">Neue Gruppe</button>
                      <button onClick={startLohnexportGruppeBearbeiten} className="btn">Bearbeiten</button>
                      <button onClick={deleteLohnexportGruppe} className="btn" style={{ color: '#b91c1c' }}>Löschen</button>
                    </div>
                  </div>

                  <div>
                    {!editingLohnexportGruppe ? (
                      <div style={{ color: '#6b7280', fontSize: '0.9rem', padding: '0.5rem' }}>
                        Gruppe auswählen oder neue Gruppe anlegen.
                      </div>
                    ) : (
                      <>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Gruppendaten</label>
                        <input
                          className="input"
                          value={editingLohnexportGruppe.name}
                          onChange={(e) => setEditingLohnexportGruppe({ ...editingLohnexportGruppe, name: e.target.value })}
                          placeholder="Gruppenname"
                          style={{ marginBottom: '0.75rem' }}
                        />
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                          <div>
                            <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>Mitarbeiterliste</div>
                            <div style={{ border: '1px solid #d1d5db', borderRadius: '0.5rem', maxHeight: '260px', overflowY: 'auto', padding: '0.5rem' }}>
                              {availableMitarbeiterForEditing.map((m) => (
                                <button
                                  key={`avail-${m.id}`}
                                  onClick={() => addMitarbeiterToEditingGruppe(Number(m.id))}
                                  className="btn"
                                  style={{ width: '100%', marginBottom: '0.35rem', justifyContent: 'space-between' }}
                                >
                                  <span>{`${m.first_name || ''} ${m.last_name || ''}`.trim()}</span>
                                  <span>+</span>
                                </button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>Gruppenliste</div>
                            <div style={{ border: '1px solid #d1d5db', borderRadius: '0.5rem', maxHeight: '260px', overflowY: 'auto', padding: '0.5rem' }}>
                              {selectedMitarbeiterForEditing.map((m) => (
                                <button
                                  key={`sel-${m.id}`}
                                  onClick={() => removeMitarbeiterFromEditingGruppe(Number(m.id))}
                                  className="btn"
                                  style={{ width: '100%', marginBottom: '0.35rem', justifyContent: 'space-between' }}
                                >
                                  <span>{`${m.first_name || ''} ${m.last_name || ''}`.trim()}</span>
                                  <span>×</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
                          <button onClick={() => setEditingLohnexportGruppe(null)} className="btn" disabled={savingLohnexportGruppe}>Abbrechen</button>
                          <button onClick={saveLohnexportGruppe} className="btn btn-primary" disabled={savingLohnexportGruppe}>
                            Speichern
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                  <button
                    className="btn btn-primary"
                    onClick={() => setLohnexportStep(2)}
                    disabled={!selectedLohnexportGruppe}
                  >
                    Weiter
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ marginBottom: '0.75rem' }}>
                  <div><strong>Gruppe:</strong> {selectedLohnexportGruppe?.name || '-'}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0.35rem 0' }}>
                    <strong>Monat:</strong>
                    <input
                      type="month"
                      className="input"
                      value={lohnexportMonth}
                      onChange={(e) => setLohnexportMonth(e.target.value)}
                      style={{ maxWidth: '220px' }}
                      disabled={exportingLohnexport}
                    />
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0.35rem 0' }}>
                    <input
                      type="checkbox"
                      checked={fillPayrollToMonthEnd}
                      onChange={(e) => setFillPayrollToMonthEnd(e.target.checked)}
                      disabled={exportingLohnexport}
                    />
                    <span>Monat bis zum Ende mit Regelstunden auffüllen!</span>
                  </label>
                  <div><strong>Zeitraum:</strong> {formatPeriodLabel(parseExportMonth(lohnexportMonth) || currentDate)}</div>
                  <div><strong>Mitarbeiter:</strong> {(selectedLohnexportGruppe?.mitarbeiter || []).length}</div>
                </div>
                <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '1rem' }}>
                  {(selectedLohnexportGruppe?.mitarbeiter || []).length === 0 ? (
                    <div style={{ color: '#6b7280' }}>Keine Mitarbeiter in dieser Gruppe.</div>
                  ) : (
                    (selectedLohnexportGruppe.mitarbeiter || []).map((id) => (
                      <div key={`member-${id}`} style={{ padding: '0.2rem 0' }}>
                        {getPersonNameById(id)}
                      </div>
                    ))
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <button className="btn" onClick={() => setLohnexportStep(1)} disabled={exportingLohnexport}>
                    Zurück
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => exportPayrollForGruppe(selectedLohnexportGruppe)}
                    disabled={exportingLohnexport || !selectedLohnexportGruppe}
                  >
                    {exportingLohnexport ? 'Export läuft...' : 'Export starten'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MonthView({ days, getEntriesForDate, getEventsForDate, getTagOptionForDate, getHolidayInfoForDate, descriptionMaxLength, editingEntry, setEditingEntry, saveEntry, deleteEntry, createNewEntry, baustellen }) {
  const weekDays = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

  return (
    <div className="card">
      {/* Verwende CSS Grid mit unterschiedlichen Spaltenbreiten */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 0.5fr 0.5fr', // Mo-Fr volle Breite, Sa-So halbe Breite
        gap: '1px',
        background: '#e5e7eb'
      }}>
        {/* Wochentage-Header */}
        {weekDays.map((day, index) => (
          <div
            key={day}
            style={{
              background: (index === 5 || index === 6) ? '#e5e7eb' : '#f9fafb',
              padding: '0.5rem',
              textAlign: 'center',
              fontWeight: '600'
            }}
          >
            {day}
          </div>
        ))}

        {/* Tage */}
        {days.map((day, index) => (
          <DayCell
            key={index}
            day={day}
            entries={day ? getEntriesForDate(day) : []}
            calendarEvents={day ? getEventsForDate(day) : []}
            tagOption={day ? getTagOptionForDate(day) : null}
            holidayInfo={day ? getHolidayInfoForDate(day) : null}
            descriptionMaxLength={descriptionMaxLength}
            editingEntry={editingEntry}
            setEditingEntry={setEditingEntry}
            saveEntry={saveEntry}
            deleteEntry={deleteEntry}
            createNewEntry={createNewEntry}
            baustellen={baustellen}
          />
        ))}
      </div>

      {/* Status-Legende */}
      <div style={{
        marginTop: '1.5rem',
        padding: '1rem',
        background: '#f9fafb',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb'
      }}>
      <div style={{ fontWeight: '600', marginBottom: '0.75rem', fontSize: '0.875rem' }}>
        Status-Legende:
      </div>
      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
        {Object.entries(STATUS_SYMBOLS).map(([statusKey, statusMeta]) => {
          const StatusIcon = statusMeta.Icon;
          const color = STATUS_COLORS[statusKey]?.border || '#6b7280';
          return (
            <div key={statusKey} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{
                width: '1.5rem',
                height: '1.5rem',
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <StatusIcon size={14} color={color} />
              </div>
              <span style={{ fontSize: '0.875rem', color }}>
                {statusMeta.label}
              </span>
            </div>
          );
        })}
      </div>
        <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: '#6b7280' }}>
          Nur Einträge mit Status "Entwurf" und "Eingereicht" können bearbeitet und gelöscht werden.
        </div>
      </div>
    </div>
  );
}

function DayCell({ day, entries, calendarEvents, tagOption, holidayInfo, descriptionMaxLength, editingEntry, setEditingEntry, saveEntry, deleteEntry, createNewEntry, baustellen }) {
  const [localEdit, setLocalEdit] = useState(null);

  if (!day) {
    return <div style={{ background: '#f9fafb', minHeight: '100px' }} />;
  }

  const totalHours = entries.reduce((sum, e) => sum + parseFloat(e.stunden || 0), 0);
  const isToday = day.toDateString() === new Date().toDateString();
  const dayOfWeek = day.getDay();
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Sonntag oder Samstag

  return (
    <div style={{
      background: isToday ? '#eff6ff' : (isWeekend ? '#f3f4f6' : 'white'),
      minHeight: '120px',
      padding: '0.5rem',
      position: 'relative',
      border: isToday ? '2px solid #3b82f6' : 'none',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{ fontWeight: '600', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
        {day.getDate()}
      </div>

      {holidayInfo && (
        <div
          style={{
            fontSize: '0.7rem',
            fontWeight: 700,
            color: '#92400e',
            background: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: '0.35rem',
            padding: '0.2rem 0.35rem',
            marginBottom: '0.35rem',
          }}
          title={holidayInfo.events.map((event) => `${event.title} (${event.calendar_name})`).join('\n')}
        >
          Feiertag
        </div>
      )}

      {entries.length > 0 && (
        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem' }}>
          {totalHours}h
        </div>
      )}

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {/* Calendar Events */}
        {calendarEvents && calendarEvents.map((event, idx) => (
            <div key={`event-${idx}`}>
              <div
                style={{
                  fontSize: '0.65rem',
                  padding: '0.25rem',
                  marginBottom: '0.25rem',
                  background: event.calendar_color || '#e5e7eb',
                  borderLeft: `3px solid ${event.calendar_color || '#6b7280'}`,
                  borderRadius: '0.25rem',
                  opacity: 0.9,
                  color: '#1f2937',
                  fontWeight: '500',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                title={`${event.title} (${event.calendar_name})`}
              >
                {event.title}
              </div>
            </div>
        ))}

        {/* Time Entries */}
        {entries.map(entry => (
          <EntryCard
            key={entry.id}
            entry={entry}
            tagOption={tagOption}
            isEditing={editingEntry?.id === entry.id}
            onEdit={() => {
              setLocalEdit({ ...entry });
              setEditingEntry(entry);
            }}
            onSave={() => {
              if (localEdit) {
                saveEntry(localEdit);
              }
            }}
            onCancel={() => {
              setEditingEntry(null);
              setLocalEdit(null);
            }}
            onDelete={() => deleteEntry(entry, tagOption)}
            localEdit={localEdit}
            setLocalEdit={setLocalEdit}
            descriptionMaxLength={descriptionMaxLength}
            baustellen={baustellen}
          />
        ))}
      </div>

      {/* Plus-Button am unteren Rand */}
      <button
        onClick={() => createNewEntry(day)}
        style={{
          marginTop: 'auto',
          width: '100%',
          padding: '0.25rem',
          background: '#e5e7eb',
          border: 'none',
          borderRadius: '0.25rem',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#6b7280'
        }}
        title="Neuen Eintrag hinzufügen"
      >
        <Plus size={16} />
      </button>
    </div>
  );
}

function darkenHexColor(hex, amount) {
  if (!hex) return '#111827';
  const safe = hex.startsWith('#') ? hex.slice(1) : hex;
  if (safe.length !== 6) return '#111827';
  const num = parseInt(safe, 16);
  if (Number.isNaN(num)) return '#111827';
  const r = Math.max(0, Math.min(255, (num >> 16) - amount));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) - amount));
  const b = Math.max(0, Math.min(255, (num & 0xff) - amount));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

function getTextColor(hex) {
  if (!hex) return '#111827';
  const safe = hex.startsWith('#') ? hex.slice(1) : hex;
  if (safe.length !== 6) return '#111827';
  const num = parseInt(safe, 16);
  if (Number.isNaN(num)) return '#111827';
  const r = num >> 16;
  const g = (num >> 8) & 0xff;
  const b = num & 0xff;
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b);
  return luminance > 160 ? '#111827' : '#f9fafb';
}

function compareByLabelDe(a, b) {
  return String(a || '').localeCompare(String(b || ''), 'de', { sensitivity: 'base' });
}

function EntryCard({ entry, tagOption, isEditing, onEdit, onSave, onCancel, onDelete, localEdit, setLocalEdit, descriptionMaxLength, baustellen }) {
  const status = entry.status || 'ENTWURF';
  const isEditable = status === 'ENTWURF' || status === 'EINGEREICHT';
  const selectableBaustellen = baustellen
    .filter(b => ['AKTIV', 'GEPLANT'].includes(b.status))
    .slice()
    .sort((a, b) => compareByLabelDe(a.kunde_name || a.name, b.kunde_name || b.name) || compareByLabelDe(a.nummer, b.nummer));
  const baustelle = baustellen.find(b => b.id === entry.baustelle);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const hasTagOption = tagOption && isOptionEntryForTag(entry, tagOption);
  const tagOptionName = hasTagOption ? (tagOption?.option_details?.name || '') : '';
  const tagOptionKurz = hasTagOption ? (tagOption?.option_details?.kurz || '') : '';
  const tagOptionColor = hasTagOption ? (tagOption?.option_details?.farbe || '#3B82F6') : '#3B82F6';
  const baustelleColor = baustelle?.farbe || '#3B82F6';
  const effectiveColor = hasTagOption ? tagOptionColor : baustelleColor;
  const cardBorder = darkenHexColor(effectiveColor, 20);
  const cardText = getTextColor(effectiveColor);
  const statusMeta = STATUS_SYMBOLS[status] || STATUS_SYMBOLS.ENTWURF;
  const StatusIcon = statusMeta.Icon;
  const fullDescription = entry.beschreibung || '';
  const maxLen = Math.max(1, parseInt(descriptionMaxLength, 10) || 40);
  const isLongDescription = fullDescription.length > maxLen;
  const shortDescription = isLongDescription ? fullDescription.slice(0, maxLen) : fullDescription;

  if (isEditing && localEdit) {
    return (
      <div style={{
        background: effectiveColor,
        border: `1px solid ${cardBorder}`,
        borderRadius: '0.25rem',
        padding: '0.5rem',
        marginBottom: '0.5rem',
        fontSize: '0.75rem'
      }}>
        <div style={{ marginBottom: '0.5rem' }}>
          <select
            value={localEdit.baustelle || ''}
            onChange={(e) => setLocalEdit({ ...localEdit, baustelle: parseInt(e.target.value) })}
            style={{ width: '100%', padding: '0.25rem', fontSize: '0.75rem', marginBottom: '0.25rem' }}
          >
            <option value="">Baustelle...</option>
            {selectableBaustellen.map(b => (
              <option
                key={b.id}
                value={b.id}
                style={{ backgroundColor: b.farbe || '#3B82F6', color: '#111827' }}
              >
                {getBaustelleDisplayName(b)}
              </option>
            ))}
          </select>

          <input
            type="number"
            step="0.25"
            value={localEdit.stunden}
            onChange={(e) => setLocalEdit({ ...localEdit, stunden: e.target.value })}
            style={{ width: '100%', padding: '0.25rem', fontSize: '0.75rem', marginBottom: '0.25rem' }}
            placeholder="Stunden"
          />

          <input
            type="text"
            value={localEdit.beschreibung}
            onChange={(e) => setLocalEdit({ ...localEdit, beschreibung: e.target.value })}
            style={{ width: '100%', padding: '0.25rem', fontSize: '0.75rem' }}
            placeholder="Beschreibung"
          />
        </div>

        <div style={{ display: 'flex', gap: '0.25rem' }}>
          <button onClick={onSave} style={{ padding: '0.25rem', fontSize: '0.75rem', flex: 1 }}>
            <Save size={12} />
          </button>
          <button onClick={onCancel} style={{ padding: '0.25rem', fontSize: '0.75rem', flex: 1 }}>
            <X size={12} />
          </button>
          {isEditable && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              style={{
                padding: '0.25rem',
                fontSize: '0.75rem',
                flex: 1,
                background: '#fecaca',
                color: '#dc2626'
              }}
              title="Löschen"
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={isEditable ? onEdit : undefined}
      style={{
        background: effectiveColor,
        border: `1px solid ${cardBorder}`,
        borderRadius: '0.25rem',
        padding: '0.4rem',
        marginBottom: '0.5rem',
        cursor: isEditable ? 'pointer' : 'default',
        fontSize: '0.75rem',
        position: 'relative'
      }}
    >
      <div style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0.12,
        pointerEvents: 'none'
      }}>
        <StatusIcon size={42} color={cardText} />
      </div>
      {/* Zeile 1: Titel + Edit-Icon */}
      <div style={{
        fontWeight: '600',
        color: cardText,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '0.25rem'
      }}>
        <span>
          {hasTagOption
            ? `${tagOptionName}${tagOptionKurz ? ` (${tagOptionKurz})` : ''}`
            : getBaustelleDisplayName(baustelle)}
        </span>
        {isEditable && <Edit2 size={10} style={{ color: cardText, flexShrink: 0 }} />}
      </div>

      {/* Zeile 2: Stunden, Beschreibung, Löschen-Button */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        color: cardText,
        fontSize: '0.7rem'
      }}>
        <span style={{ fontWeight: '500', flexShrink: 0 }}>{entry.stunden}h</span>
        <span style={{
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {fullDescription ? (
            <>
              {shortDescription}
              {isLongDescription && (
                <>
                  {' '}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDescriptionModal(true);
                    }}
                    title={fullDescription}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: cardText,
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      padding: 0,
                      fontSize: '0.7rem'
                    }}
                  >
                    [mehr]
                  </button>
                </>
              )}
            </>
          ) : (
            '-'
          )}
        </span>
        {isEditable && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            style={{
              padding: '0.125rem',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: '#dc2626',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center'
            }}
            title="Löschen"
          >
            <Trash2 size={10} />
          </button>
        )}
      </div>

      {showDescriptionModal && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            setShowDescriptionModal(false);
          }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            zIndex: 3000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="card"
            style={{ width: '100%', maxWidth: '680px', maxHeight: '80vh', overflowY: 'auto' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h4 style={{ margin: 0, fontSize: '1rem' }}>Komplette Beschreibung</h4>
              <button onClick={() => setShowDescriptionModal(false)} className="btn">
                <X size={16} /> Schließen
              </button>
            </div>
            <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, color: '#111827' }}>
              {fullDescription || '-'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function YearView({ months, getEntriesForMonth, getTotalHoursForMonth, setCurrentDate, setViewMode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
      {months.map(month => {
        const entries = getEntriesForMonth(month);
        const totalHours = getTotalHoursForMonth(month);
        const hasEntwurf = entries.some(e => e.status === 'ENTWURF');
        const hasEingereicht = entries.some(e => e.status === 'EINGEREICHT');
        const hasGenehmigt = entries.some(e => e.status === 'GENEHMIGT');

        return (
          <div
            key={month.getMonth()}
            className="card"
            onClick={() => {
              setCurrentDate(month);
              setViewMode('month');
            }}
            style={{
              cursor: 'pointer',
              transition: 'transform 0.2s',
              ':hover': { transform: 'scale(1.02)' }
            }}
          >
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem' }}>
              {month.toLocaleDateString('de-DE', { month: 'long' })}
            </h3>

            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#0ea5e9', marginBottom: '0.5rem' }}>
              {totalHours}h
            </div>

            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              {entries.length} Einträge
            </div>

            {/* Status-Indikatoren */}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
              {hasEntwurf && (
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: STATUS_COLORS.ENTWURF.border
                }} />
              )}
              {hasEingereicht && (
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: STATUS_COLORS.EINGEREICHT.border
                }} />
              )}
              {hasGenehmigt && (
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: STATUS_COLORS.GENEHMIGT.border
                }} />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default StundenerfassungView;
